/**
 * Main Theme Configuration
 * Central theme object that integrates all design tokens
 */

import { createTheme } from '@mui/material/styles';
import { palette } from './palette';
import { typography } from './typography';
import { components } from './components';
import { spacing, breakpoints, shadows, shape, transitions } from './foundations';
import { typography as typographyTokens } from '../tokens/dimensions';

// Create base theme with core configuration
const baseTheme = createTheme({
  palette,
  typography,
  spacing,
  breakpoints,
  shadows,
  shape,
  transitions,
});

// Create final theme (component overrides will be applied in ThemeContext with density)
const theme = createTheme(baseTheme);

// Add typography fontSize and fontWeight to theme for easy access
theme.typography.fontSize = typographyTokens.fontSize;
theme.typography.fontWeight = typographyTokens.fontWeight;

// Add custom properties to theme
theme.dimensions = {
  panel: {
    // headerHeight will be set by density config in ThemeContext
    minWidth: '200px',
    defaultWidth: '300px',
    maxWidth: '600px',
    leftSidebarWidth: 480,      // Default width for comfortable density
    leftSidebarMinWidth: 400,   // Default min width
  },
  input: {
    height: {
      small: '28px',
      medium: '32px',
      large: '40px',
    },
  },
  button: {
    height: {
      small: '24px',
      medium: '32px',
      large: '40px',
    },
  },
  icon: {
    small: 14,   // Visually balanced with text (was 16)
    medium: 17,  // ~85% for optical alignment (was 20)
    large: 20,   // Reduced for visual harmony (was 24)
  },
  dataGrid: {
    rowHeight: 36,
    headerHeight: 44,
  },
};

// Add custom mixins
theme.mixins = {
  ...theme.mixins,
  // Panel header mixin
  panelHeader: {
    padding: theme.spacing(1.5),
    // height will use theme.dimensions.panel.headerHeight
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.grey[50],
  },
  // Scrollable area mixin
  scrollable: {
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
    '&::-webkit-scrollbar': {
      width: 8,
      height: 8,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0, 0, 0, 0.2)',
      borderRadius: 4,
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
      },
    },
  },
  // Ellipsis text mixin
  ellipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Flex center mixin
  flexCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

export default theme;